// Pricing engine class (pure calculation)
// Supports backend-configured rental pricing rules with this priority:
// 1. Product metafield: custom.rental_pricing_rules
// 2. Shop metafield: custom.rental_pricing_rules
// 3. Existing shop metafield: custom.discount_pricing_data
// 4. Safe no-discount fallback
class FGRentalPricingEngine {
  constructor(data) {
    this.rawData = data || {};
    this.data = this.resolvePricingData(this.rawData);
  }

  parseMaybeJson(value) {
    if (value == null || value === '') return null;
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed);
    } catch (error) {
      return null;
    }
  }

  toNumber(value, fallback = 0) {
    if (value == null || value === '') return fallback;
    const number = Number(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(number) ? number : fallback;
  }

  normalizeDiscount(value) {
    const number = this.toNumber(value, 0);

    // Backend users may enter 10 for 10%, while existing data may use 0.1.
    // Support both formats safely.
    if (number > 1 && number <= 100) return number / 100;
    if (number < 0) return 0;
    if (number > 1) return 1;
    return number;
  }

  normalizeQuantityTiers(tiers) {
    const source = Array.isArray(tiers) ? tiers : [];
    const normalized = source
      .map((tier) => {
        if (!tier || typeof tier !== 'object' || Array.isArray(tier)) return null;

        const from = this.toNumber(
          tier.from ?? tier.min ?? tier.minQty ?? tier.min_qty ?? tier.qtyMin ?? tier.qty_min ?? tier.start,
          null
        );

        if (from == null || from < 1) return null;

        return {
          from: Math.floor(from),
          discount: this.normalizeDiscount(
            tier.discount ?? tier.discountRate ?? tier.discount_rate ?? tier.percent ?? tier.percentage ?? tier.rate ?? 0
          ),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.from - b.from);

    if (!normalized.length || normalized[0].from > 1) {
      normalized.unshift({ from: 1, discount: 0 });
    }

    return normalized;
  }

  normalizeDayDiscounts(discounts) {
    const source = Array.isArray(discounts) ? discounts : [];
    const normalized = source
      .map((row) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return null;

        const day = this.toNumber(
          row.day ?? row.from ?? row.min ?? row.minDay ?? row.min_day ?? row.start ?? row.days,
          null
        );

        if (day == null || day < 1) return null;

        return {
          day: Math.floor(day),
          discount: this.normalizeDiscount(
            row.discount ?? row.discountRate ?? row.discount_rate ?? row.percent ?? row.percentage ?? row.rate ?? 0
          ),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.day - b.day);

    if (!normalized.length || normalized[0].day > 1) {
      normalized.unshift({ day: 1, discount: 0 });
    }

    return normalized;
  }

  normalizeHandlingFees(fees) {
    const source = Array.isArray(fees) ? fees : [];
    return source
      .map((row) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return null;

        const from = this.toNumber(
          row.from ?? row.min ?? row.minQty ?? row.min_qty ?? row.qtyMin ?? row.qty_min ?? row.start,
          null
        );
        const fee = this.toNumber(row.fee ?? row.amount ?? row.price ?? row.value ?? row.handlingFee ?? row.handling_fee, null);

        if (from == null || fee == null || from < 1 || fee < 0) return null;

        return {
          from: Math.floor(from),
          fee,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.from - b.from);
  }

  normalizeRule(rule) {
    const source = rule && typeof rule === 'object' && !Array.isArray(rule) ? rule : {};

    return {
      quantityTiers: this.normalizeQuantityTiers(source.quantityTiers ?? source.quantity_tiers ?? source.qtyTiers ?? source.qty_tiers),
      dayDiscounts: this.normalizeDayDiscounts(source.dayDiscounts ?? source.day_discounts ?? source.durationDiscounts ?? source.duration_discounts),
      calculateHandlingFee: !!(source.calculateHandlingFee ?? source.calculate_handling_fee),
      handlingFees: this.normalizeHandlingFees(source.handlingFees ?? source.handling_fees),
    };
  }

  normalizePricingRulesMap(rules) {
    if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return null;

    const normalized = {};

    Object.keys(rules).forEach((key) => {
      const rule = this.normalizeRule(rules[key]);
      if (rule) normalized[key] = rule;
    });

    if (!Object.keys(normalized).length) return null;

    if (!normalized.default) {
      normalized.default = this.normalizeRule({});
    }

    return { pricingRules: normalized };
  }

  unwrapSource(source) {
    let current = this.parseMaybeJson(source);

    // Shopify JSON metafields are usually output as the value itself, but older
    // fields may be wrapped or stored as a JSON string. Unwrap common wrappers.
    for (let i = 0; i < 5; i += 1) {
      if (!current || typeof current !== 'object' || Array.isArray(current)) break;

      if (Object.prototype.hasOwnProperty.call(current, 'value')) {
        current = this.parseMaybeJson(current.value);
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(current, 'data')) {
        current = this.parseMaybeJson(current.data);
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(current, 'rules') && !Object.prototype.hasOwnProperty.call(current, 'pricingRules')) {
        current = this.parseMaybeJson(current.rules);
        continue;
      }

      break;
    }

    return current;
  }

  sourceToPricingData(source) {
    const current = this.unwrapSource(source);
    if (!current || typeof current !== 'object' || Array.isArray(current)) return null;

    if (current.pricingRules || current.pricing_rules) {
      return this.normalizePricingRulesMap(current.pricingRules ?? current.pricing_rules);
    }

    // Allow the backend JSON to be a direct map:
    // { "default": {...}, "SKU123": {...} }
    if (current.default || Object.keys(current).some((key) => current[key] && current[key].quantityTiers)) {
      const mapResult = this.normalizePricingRulesMap(current);
      if (mapResult) return mapResult;
    }

    // Allow the backend JSON to be a single default rule:
    // { "quantityTiers": [...], "dayDiscounts": [...] }
    if (current.quantityTiers || current.quantity_tiers || current.dayDiscounts || current.day_discounts) {
      return this.normalizePricingRulesMap({ default: current });
    }

    return null;
  }

  resolvePricingData(rawData) {
    const raw = this.unwrapSource(rawData) || {};

    // New wrapper emitted by button.txt. Priority is intentionally the same as
    // Handling Fee: product > shop > existing legacy field > fallback.
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const sources = [raw.product, raw.shop, raw.legacy, raw.fallback];

      for (const source of sources) {
        const resolved = this.sourceToPricingData(source);
        if (resolved) return resolved;
      }
    }

    // Backward compatibility: pricing.js can still accept the old direct JSON.
    const direct = this.sourceToPricingData(raw);
    if (direct) return direct;

    return this.sourceToPricingData({
      pricingRules: {
        default: {
          quantityTiers: [{ from: 1, discount: 0 }],
          dayDiscounts: [{ day: 1, discount: 0 }],
          calculateHandlingFee: false,
          handlingFees: [],
        },
      },
    });
  }

  getRulesForSku(sku) {
    const pricingRules = this.data && this.data.pricingRules ? this.data.pricingRules : {};
    return pricingRules[sku] || pricingRules[String(sku || '').trim()] || pricingRules.default || this.normalizeRule({});
  }

  getEffectiveQty(quantity, tiers) {
    const qty = Math.max(0, Math.floor(this.toNumber(quantity, 0)));
    const normalizedTiers = this.normalizeQuantityTiers(tiers);
    let effectiveQty = 0;

    for (let i = 0; i < normalizedTiers.length; i += 1) {
      const tier = normalizedTiers[i];
      const nextFrom = i + 1 < normalizedTiers.length ? normalizedTiers[i + 1].from : Infinity;
      const tierQty = Math.max(0, Math.min(qty, nextFrom - 1) - tier.from + 1);

      if (tierQty > 0) {
        effectiveQty += tierQty * (1 - tier.discount);
      }
    }

    return effectiveQty;
  }

  getHandlingFee(quantity, fees, enabled) {
    if (!enabled) return 0;

    const qty = Math.max(0, Math.floor(this.toNumber(quantity, 0)));
    const rows = this.normalizeHandlingFees(fees).filter((row) => qty >= row.from);
    const row = rows.slice(-1)[0];

    return row ? row.fee : 0;
  }

  getDayDiscount(day, dayDiscounts) {
    const currentDay = Math.max(1, Math.floor(this.toNumber(day, 1)));
    const anchor = this.normalizeDayDiscounts(dayDiscounts)
      .filter((row) => row.day <= currentDay)
      .sort((a, b) => a.day - b.day)
      .slice(-1)[0];

    return anchor ? anchor.discount : 0;
  }

  getPriceAfterQuantityDiscount(days, baseRate, effectiveQty, dayDiscounts) {
    const rentalDays = Math.max(0, Math.floor(this.toNumber(days, 0)));
    const dailyBase = this.toNumber(baseRate, 0) * this.toNumber(effectiveQty, 0);
    let total = 0;

    for (let day = 1; day <= rentalDays; day += 1) {
      const discount = this.getDayDiscount(day, dayDiscounts);
      const dayFactor = 1 - discount;
      total += dailyBase * dayFactor;
    }

    return total;
  }

  calculateRental(quantity, days, sku, baseRate) {
    const qty = Math.max(0, Math.floor(this.toNumber(quantity, 0)));
    const rentalDays = Math.max(0, Math.floor(this.toNumber(days, 0)));
    const rate = this.toNumber(baseRate, 0);

    if (qty <= 0 || rentalDays <= 0 || rate <= 0) {
      return {
        sku,
        effectiveQty: 0,
        handlingFee: 0,
        discountedPrice: 0,
        total: 0,
        originalPrice: 0,
        totalDiscount: 0,
        discountPct: 0,
        discountedDailyRate: 0,
      };
    }

    const rules = this.getRulesForSku(sku);
    const effectiveQty = this.getEffectiveQty(qty, rules.quantityTiers);
    const handlingFee = this.getHandlingFee(qty, rules.handlingFees, rules.calculateHandlingFee);
    const discountedPrice = this.getPriceAfterQuantityDiscount(rentalDays, rate, effectiveQty, rules.dayDiscounts);
    const total = discountedPrice + handlingFee;
    const originalPrice = rate * qty * rentalDays;
    const totalDiscount = originalPrice - discountedPrice;
    const discountedDailyRate = Math.round((discountedPrice / rentalDays / qty) * 100) / 100;
    const discountPct = originalPrice > 0 ? (totalDiscount / originalPrice) * 100 : 0;

    return {
      sku,
      effectiveQty,
      handlingFee,
      discountedPrice,
      total,
      originalPrice,
      totalDiscount,
      discountPct,
      discountedDailyRate,
    };
  }
}

// Attach to window for global access
window.FGRentalPricingEngine = FGRentalPricingEngine;
